import type { AxiosRequestConfig } from "axios";
import { axiosClient, API_BASE_URL } from "../api/axiosClient";

export const authQueryKeys = {
  userEmail: ["userEmail"] as const,
};

export type User = {
  id: string;
  name?: string;
  email?: string;
  roles?: string[];
  profile: {
    terreiro?: {
      id?: string;
    };
  };
  [k: string]: unknown;
};

type OAuthContext = {
  success: boolean;
  message?: string;
  nextStep?: "home" | "select_type";
  isNewUser?: boolean;
  mode?: "signin" | "signup";
  returnTo?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  userId?: string;
};

const buildOAuthStartUrl = (
  provider: "google" | "facebook",
  mode: "signin" | "signup",
  returnTo?: string,
) => {
  const oauthStartPath = import.meta.env.DEV
    ? API_BASE_URL.startsWith("/")
      ? `${API_BASE_URL.replace(/\/$/, "")}/auth/${provider}/start`
      : `/v1/auth/${provider}/start`
    : `/oauth/${provider}/start`;
  const url = new URL(oauthStartPath, window.location.origin);
  url.searchParams.set("mode", mode);
  url.searchParams.set("frontendOrigin", window.location.origin);
  if (returnTo) {
    url.searchParams.set("returnTo", returnTo);
  }
  return url.toString();
};

export const authRepository = {
  buildGoogleAuthStartUrl(mode: "signin" | "signup", returnTo?: string) {
    return buildOAuthStartUrl("google", mode, returnTo);
  },

  buildFacebookAuthStartUrl(mode: "signin" | "signup", returnTo?: string) {
    return buildOAuthStartUrl("facebook", mode, returnTo);
  },

  async getOAuthContext(token?: string): Promise<OAuthContext> {
    const response = await axiosClient.get("/auth/oauth/context", {
      withCredentials: true,
      params: token ? { token } : undefined,
    });
    return response.data;
  },

  async getGoogleOAuthContext(token?: string): Promise<OAuthContext> {
    return authRepository.getOAuthContext(token);
  },

  async getUserEmail(): Promise<{ email: string }> {
    try {
      const response = await axiosClient.get<{ email: string }>(
        "/auth/me/email",
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user email:", error);
      throw error;
    }
  },

  async changeUserEmail(newEmail: string): Promise<{ success: boolean }> {
    try {
      const response = await axiosClient.post<{ success: boolean }>(
        "/auth/change-email",
        { newEmail },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("Error changing user email:", error);
      throw error;
    }
  },

  async changeUserPassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean }> {
    try {
      const response = await axiosClient.post<{ success: boolean }>(
        "/auth/change-password",
        { currentPassword, newPassword },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      // The axios error carries the request body; log only the status so passwords never reach logs.
      console.error(
        "Error changing user password:",
        (error as { response?: { status?: number } })?.response?.status ?? "network error",
      );
      throw error;
    }
  },

  async getMe(signal?: AbortSignal, terreiroId?: string): Promise<User> {
    const config: AxiosRequestConfig = {
      signal,
      // mark this request so the auth interceptor will not attempt a refresh for it
      headers: {
        "x-skip-refresh": "1",
      },
      params: terreiroId ? { terreiroId } : undefined,
    };
    // API returns { message, userId, profile, roles } - we need to map it to User shape
    const resp = await axiosClient.get<{
      message: string;
      userId?: string;
      profile: Record<string, unknown>;
      roles: string[];
    }>("/profile/me", config);

    const { userId, profile, roles } = resp.data;

    return {
      id: userId || "",
      name: profile?.firstName
        ? `${profile.firstName} ${profile.lastName || ""}`.trim()
        : undefined,
      email: (profile?.email as string) || undefined,
      roles: roles || [],
      profile: profile as User["profile"],
    };
  },

  // refresh uses a raw axios instance (no interceptors) to avoid cycles.
  // IMPORTANT: do NOT use axiosClient here — it has the 401 interceptor
  // which would recursively attempt refresh on failure.
  async refresh(): Promise<void> {
    const { default: axios } = await import("axios");
    const { API_BASE_URL } = await import("../api/axiosClient");
    const { REFRESH_TIMEOUT_MS } = await import("../auth/sessionRefresh");
    await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
      withCredentials: true,
      timeout: REFRESH_TIMEOUT_MS,
      headers: {
        "Content-Type": "application/json",
      },
    });
  },

  async logout(): Promise<void> {
    await axiosClient.post("/auth/logout");
  },
};
